import { CheckCircle2Icon, XCircleIcon, CircleDotIcon } from "lucide-react";
import { Card } from "@/components/ui/card";
import type {
  PracticeLabCheckResult,
  PracticeLabCheckSpec,
  PracticeLabStepStateResponse,
} from "@/types/practice-lab";

// Resolve a live check result to pass / fail / unknown. Authors report either a
// boolean `passed` or a `status` string, so accept both defensively.
function checkPassed(result?: PracticeLabCheckResult): boolean | null {
  if (!result) return null;
  if (typeof result.passed === "boolean") return result.passed;
  if (typeof result.status === "string") {
    const s = result.status.toLowerCase();
    if (["pass", "passed", "ok", "green", "success"].includes(s)) return true;
    if (["fail", "failed", "error", "red"].includes(s)) return false;
  }
  return null;
}

function asText(value: unknown): string | undefined {
  return typeof value === "string" && value.length > 0 ? value : undefined;
}

// The live checklist. Joins the STATIC check_spec (authored titles + offline fix
// instructions) with the LIVE per-check results (from the CLI over SSE) by check
// `id`. Passing rows are green, failing rows are red with the authored fix.
export function CheckPanel({
  specs,
  state,
}: {
  specs: PracticeLabCheckSpec[];
  state: PracticeLabStepStateResponse | null;
}) {
  const results = new Map<string, PracticeLabCheckResult>();
  state?.checks.forEach((c) => {
    if (typeof c.id === "string") results.set(c.id, c);
  });

  return (
    <Card className="gap-0 overflow-hidden py-0">
      <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border-b px-5 py-3">
        <span className="font-semibold">
          Checks{state ? ` — step ${state.order}` : ""}
        </span>
        <span className="text-xs text-muted-foreground">
          {state?.last_check_at ? "ran locally · unlimited" : "not run yet"}
        </span>
      </div>
      <ul className="divide-y">
        {specs.map((spec, i) => {
          const result = spec.id ? results.get(spec.id) : undefined;
          const passed = checkPassed(result);
          const title =
            asText(spec.title) ?? asText(spec.pass) ?? spec.id ?? `Check ${i + 1}`;
          const desc =
            asText(spec.description) ??
            asText(result?.detail) ??
            asText(result?.message);
          const fix = asText(result?.fix) ?? asText(spec.fail);

          return (
            <li key={spec.id ?? i} className="flex items-start gap-3 px-5 py-4">
              {passed === true ? (
                <CheckCircle2Icon className="mt-0.5 size-5 shrink-0 text-green-600" />
              ) : passed === false ? (
                <XCircleIcon className="mt-0.5 size-5 shrink-0 text-red-600" />
              ) : (
                <CircleDotIcon className="mt-0.5 size-5 shrink-0 text-muted-foreground/50" />
              )}
              <div className="min-w-0 flex-1 space-y-2">
                <div>
                  <p className="text-sm font-medium break-words">{title}</p>
                  {desc && (
                    <p className="text-sm text-muted-foreground break-words">{desc}</p>
                  )}
                </div>
                {passed === false && fix && (
                  <div className="break-words rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950/30 dark:text-red-200">
                    <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-red-600 dark:text-red-400">
                      How to fix
                    </p>
                    {fix}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}
